import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { imageUrl } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";

export default function Lightbox({ images, index, onClose, onIndex }) {
  const { t, lang } = useLang();
  const total = images.length;

  const prev = useCallback(
    () => onIndex((index - 1 + total) % total),
    [index, total, onIndex]
  );
  const next = useCallback(
    () => onIndex((index + 1) % total),
    [index, total, onIndex]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [prev, next, onClose]);

  if (typeof document === "undefined") return null;
  const img = images[index];
  if (!img) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={lang === "id" ? "Galeri proyek" : "Project gallery"}
      className="fixed inset-0 z-[60] bg-[#1b1c1e]/97 flex flex-col"
      data-testid="lightbox"
    >
      <div className="flex items-center justify-between px-6 md:px-10 py-5 text-white/80">
        <div className="text-xs uppercase tracking-wider">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={lang === "id" ? "Tutup" : "Close"}
          className="p-2 -mr-2 hover:text-white"
          data-testid="lightbox-close"
        >
          <X size={22} />
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center px-4 md:px-16">
        {total > 1 && (
          <button
            type="button"
            onClick={prev}
            aria-label={lang === "id" ? "Sebelumnya" : "Previous"}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={36} />
          </button>
        )}
        <img
          src={imageUrl(img.path)}
          alt={t(img.caption) || ""}
          className="max-h-[78vh] max-w-full object-contain select-none"
          draggable={false}
        />
        {total > 1 && (
          <button
            type="button"
            onClick={next}
            aria-label={lang === "id" ? "Berikutnya" : "Next"}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white"
            data-testid="lightbox-next"
          >
            <ChevronRight size={36} />
          </button>
        )}
      </div>

      {t(img.caption) && (
        <div
          className="px-6 md:px-10 py-6 text-white/70 text-sm md:text-[15px] max-w-[80ch]"
          data-testid="lightbox-caption"
        >
          {t(img.caption)}
        </div>
      )}
    </div>,
    document.body
  );
}
